import React, {useCallback, useMemo} from "react";

import {IEvent, IGame, IGameShowResponse} from "@webng-types/write-model";
import {ErrorBoundary, useEnvironment} from "@webng/react-app-common";
import {OnMediaClickHandler} from "./content/MultiMediaContentBlockView";
import {LiveblogMilestoneListItem} from "./LiveblogMilestoneListItem";
import {LiveblogEventRow, LiveblogEventRowExportedProps} from "./LiveblogEventRow";
import {LiveblogMilestoneRow} from "./LiveblogMilestoneRow";
import {LiveblogTagFilter, OnChangeTagsHandler} from "./LiveblogTagFilter";
import {LiveblogMilestoneList} from "./LiveblogMilestoneList";
import {useTranslation} from "react-i18next";
import TickarooLogo from "./assets/TickarooLogo";
import {CircleLoader} from "@webng/icons-common";
import {ArrowDown, ChevronDown, ChevronsUp, ChevronUp, RefreshCw} from "react-feather";
import {MasonryContainer} from "./MasonryContainer";
import {Extension} from "./extensions/Extension";
import Scoreboard from "./scoreboard/Scoreboard";
import {LiveblogGameSummary} from "./LiveblogGameSummary";
import {LiveblogGameSummaryItem} from "./LiveblogGameSummaryItem";
import {LiveblogHighlightSummary} from "./LiveblogHighlightSummary";
import {LiveblogHighlightSummaryItem} from "./LiveblogHighlightSummaryItem";
import {LiveblogHighlightSliderSummary} from "./LiveblogHighlightSliderSummary";
import {LiveblogHighlightSliderSummaryItem} from "./LiveblogHighlightSliderSummaryItem";
import {getHighlightPostText} from "./utils/eventUtils";
import {EventExtensionRenderer} from "./extensions/EventExtensionRenderer";
import {EventTagNavigationType, HighlightSummaryType, SortControlsType, SortOrder} from "./types";
import {LiveblogRenderPortalTypes, useLiveblogRenderPortals} from "./LiveblogRenderContext";
import {createPortal} from "react-dom";
import {useLiveblogWrapperClassNames} from "./LiveblogWrapper";

export type OnSummaryItemClickHandler = (e: IEvent) => void

interface LiveblogRowSwitcherProps extends LiveblogEventRowExportedProps {
  game: IGame
  event: IEvent
  reactionCounts?: {
    [reactionId: string]: number;
  }
}

function ErrorComponent() {
  return <React.Fragment/>
}

function LiveblogRowSwitcher({game, event, ...props}: LiveblogRowSwitcherProps) {
  if (event.highlight === "milestone") {
    return <LiveblogMilestoneRow event={event}/>
  } else {
    return <LiveblogEventRow game={game} event={event} {...props} />
  }
}

interface LiveblogProps {
  game: IGame
  rows: IEvent[]
  milestones?: IEvent[]
  goals?: IEvent[]
  highlights?: IEvent[]
  selectedTags?: string[]
  pendingUpdate?: IGameShowResponse
  onBackToLiveClick?: () => void
  onLoadMoreTopClick?: () => void
  onLoadMoreBottomClick?: () => void
  onApplyPendingUpdates?: () => void
  onMilestoneClick?: OnSummaryItemClickHandler
  onHighlightClick?: OnSummaryItemClickHandler
  onGameSummaryClick?: OnSummaryItemClickHandler
  onMediaClick?: OnMediaClickHandler
  onChangeTags?: OnChangeTagsHandler
  onSortChange?: (sort: SortOrder) => void
  loadMoreTopLink?: string
  loadMoreBottomLink?: string
  backToLiveLink?: string
  isLoadingBackToLive?: boolean
  isLoadingMoreTop?: boolean
  isLoadingMoreBottom?: boolean
  isLoadingPendingUpdate?: boolean
  isLoadingSortChange?: boolean
  highlightSummaryType?: HighlightSummaryType,
  sportsShowScoreboard?: boolean
  sportsShowScorerList?: boolean
  automaticSummaryHighlightsLimit: number
  eventTagNavigation?: EventTagNavigationType
  extension?: Extension
  reactionCounts?: {
    [eventId: string]: {
      [reactionId: string]: number;
    };
  }
  sortControls?: SortControlsType
  currentSort?: SortOrder
}

function usePreventDefaultCallback(callback: (() => void)|undefined) {
  return useCallback((e: React.UIEvent) => {
    e.preventDefault();
    e.stopPropagation();
    callback && callback()
  }, [callback])
}

function inferLanguage(locale: string){
  if(locale === "de"){
    return "de";
  }
  return "en";
}

function MaybeLiveblogHighlightSliderSummary({highlightSummaryType, highlights, game, onHighlightClick}: {
  highlightSummaryType?: HighlightSummaryType,
  highlights: IEvent[] | undefined,
  game: IGame,
  onHighlightClick?: OnSummaryItemClickHandler
}) {

  const displayHighlights = useMemo(() => {
    if(highlightSummaryType === "highlights-slider" && highlights && highlights.length > 0) {
      return highlights.filter(h => getHighlightPostText(h) !== "")
    } else {
      return []
    }
  }, [highlightSummaryType, highlights])

  return <>
    {displayHighlights.length > 0 &&
      <LiveblogHighlightSliderSummary highlightCount={displayHighlights.length}>
        {displayHighlights.map(e => <ErrorBoundary key={"highlight-slider" + e.local_id} FallbackComponent={ErrorComponent}>
        <LiveblogHighlightSliderSummaryItem event={e} game={game} onClick={onHighlightClick}/>
      </ErrorBoundary>)}
      </LiveblogHighlightSliderSummary>
    }
  </>;
}

function usePotentialPortal(type: LiveblogRenderPortalTypes): (node: React.ReactNode) => React.ReactNode {
  const portalTypes = useLiveblogRenderPortals()
  const {style, className} = useLiveblogWrapperClassNames()

  return useCallback((node: React.ReactNode)=> {
    const portals = portalTypes ? portalTypes[type] : undefined
    if(portals && portals.length > 0) {
      return portals.map(portal => createPortal(<div className={className} style={style as any}>{node}</div>, portal.element))
    } else {
      return node
    }
  }, [className, portalTypes, style, type])
}

export function Liveblog({
                           game, milestones, goals, highlights, pendingUpdate, rows, selectedTags,
                           onBackToLiveClick, onLoadMoreTopClick, onLoadMoreBottomClick, onApplyPendingUpdates,
                           isLoadingBackToLive, isLoadingMoreTop, isLoadingMoreBottom, isLoadingPendingUpdate, isLoadingSortChange,
                           onMilestoneClick, onHighlightClick, onGameSummaryClick, onMediaClick, onChangeTags, onSortChange,
                           backToLiveLink, loadMoreTopLink, loadMoreBottomLink, highlightSummaryType, sportsShowScorerList, sportsShowScoreboard, automaticSummaryHighlightsLimit,
                           eventTagNavigation, extension, reactionCounts, sortControls, currentSort}: LiveblogProps) {
  const {t, i18n} = useTranslation("liveblog");
  const env = useEnvironment();


  const onBackToLiveClickPreventDefault = usePreventDefaultCallback(onBackToLiveClick)
  const onLoadMoreTopClickPreventDefault = usePreventDefaultCallback(onLoadMoreTopClick);
  const onLoadMoreBottomClickPreventDefault = usePreventDefaultCallback(onLoadMoreBottomClick);
  const onSortAscClickPreventDefault = usePreventDefaultCallback(onSortChange ? () => onSortChange('asc' as SortOrder) : undefined);
  const onSortDescClickPreventDefault = usePreventDefaultCallback(onSortChange ? () => onSortChange('desc' as SortOrder) : undefined);

  const scoreboardPortal = usePotentialPortal('scoreboard')
  const tagNavigationPortal = usePotentialPortal('eventTagNavigation')
  const summaryPortal = usePotentialPortal('automaticSummary')
  const sortControlsPortal = usePotentialPortal('sortControls')

  if (game) {
    return <React.Fragment>

      {pendingUpdate &&
        <div className="tik4-pending-update">
          <button className="tik4-pending-update__button tik4-button tik4-button--primary"
                  onClick={onApplyPendingUpdates} type="button">
            <RefreshCw className={`tik4-icon tik4-icon--mr ${isLoadingPendingUpdate ? "tik4-spin" : ''}`}/>
            {t('liveblog.pending_update.button')}
          </button>
        </div>
      }

      <div className="tik4-blog">
        {sportsShowScoreboard && game?.scoreboard &&
          scoreboardPortal(<Scoreboard scoreboard={game.scoreboard} isMain startDate={game.meta_info?.starts_at}/>)
        }
        {/*currently only games with TeamGameScoreboards are supported*/}
        {sportsShowScorerList && goals && goals.length > 0 && game.scoreboard?._type === "Tik::Model::Content::Scoreboard::TeamGameScoreboard" &&
          <LiveblogGameSummary>
            {goals.map(goalEvent => <ErrorBoundary key={"goal" + goalEvent.local_id} FallbackComponent={ErrorComponent}>
              <LiveblogGameSummaryItem game={game} event={goalEvent} onClick={onGameSummaryClick}/>
            </ErrorBoundary>)}
          </LiveblogGameSummary>
        }

        {(eventTagNavigation && (game?.use_event_tags) && game?.event_tags && game.event_tags.filter(t => t.show_in_navigation ?? true).length > 0) &&
          tagNavigationPortal(<LiveblogTagFilter tags={game.event_tags.filter(t => t.show_in_navigation ?? true)?.sort((a,b) => a.name.localeCompare(b.name))} onChangeTags={onChangeTags} selectedTags={selectedTags} eventTagNavigation={eventTagNavigation}/>)
        }


        {highlightSummaryType === "chapters" && milestones && milestones.length > 0 && summaryPortal(
          <LiveblogMilestoneList game={game}>
            {milestones.map(e => <ErrorBoundary key={"milestone" + e.local_id} FallbackComponent={ErrorComponent}>
              <LiveblogMilestoneListItem event={e} onClick={onMilestoneClick}/>
            </ErrorBoundary>)}
          </LiveblogMilestoneList>)
        }

        {highlightSummaryType === "highlights" && highlights && highlights.length > 0 && summaryPortal(
          <LiveblogHighlightSummary game={game}>
            {highlights.filter(e => getHighlightPostText(e) !== "").slice(0, automaticSummaryHighlightsLimit).map(e => <ErrorBoundary key={"highlight" + e.local_id} FallbackComponent={ErrorComponent}>
              <LiveblogHighlightSummaryItem event={e} onClick={onHighlightClick}/>
            </ErrorBoundary>)}
          </LiveblogHighlightSummary>)
        }


        {summaryPortal(<MaybeLiveblogHighlightSliderSummary highlightSummaryType={highlightSummaryType}
                                             highlights={highlights?.filter(e => getHighlightPostText(e) !== "").slice(0, automaticSummaryHighlightsLimit)}
                                             onHighlightClick={onHighlightClick}
                                             game={game}
        />)}

        {(loadMoreTopLink || backToLiveLink) &&
          <div className="tik4-loading-top">
            {backToLiveLink &&
              <a
                className="tik4-loading-top__button tik4-loading-top__button--back-to-live tik4-button tik4-button--primary"
                href={backToLiveLink} onClick={onBackToLiveClickPreventDefault}>
                {isLoadingBackToLive ?
                  <CircleLoader className="tik4-icon tik4-icon--mr tik4-spin"/>
                  :
                  <ChevronsUp className="tik4-icon tik4-icon--mr"/>
                }
                {t('liveblog.back_to_live.button')}
              </a>
            }
            {loadMoreTopLink &&
              <a
                className="tik4-loading-top__button tik4-loading-top__button--load-more tik4-button tik4-button--primary"
                href={loadMoreTopLink} onClick={onLoadMoreTopClickPreventDefault}>
                {isLoadingMoreTop ?
                  <CircleLoader className="tik4-icon tik4-icon--mr tik4-spin"/>
                  :
                  <ChevronUp className="tik4-icon tik4-icon--mr"/>
                }
                {t('liveblog.load_more_top.button')}
              </a>
            }
          </div>
        }

        {sortControls === 'show' &&
          sortControlsPortal(
            <div className="tik4-elc">
              {/* elc: event list controls */}
              <div className="tik4-elc__l">
                {/* elc__l: event list controls left */}
              </div>
              <div className="tik4-elc__r">
                {/* elc__r: event list controls right */}
                <div className="tik4-elsc">
                  <div className="tik4-elsc__l">
                    <span className="tik4-elsc__l__t">{t('liveblog.sort.label')}</span>
                  </div>
                  <div className="tik4-elsc__c tik4-button-group">
                    <button
                      className={`tik4-elsc__b tik4-button tik4-button-group__button ${currentSort === 'desc' ? 'tik4-button--outline-dark tik4-button-group__button--active' : 'tik4-button--outline-muted'} tik4-button--sm`}
                      onClick={onSortDescClickPreventDefault} type="button" disabled={isLoadingSortChange}>
                      <ArrowDown className="tik4-elsc__b__i tik4-icon tik4-icon--mr"/>
                      <span className="tik4-elsc__c__b__t">
                        <span className="tik4-elsc__c__b__t__d">
                          {t('liveblog.sort.latest')}
                        </span>
                        <span className="tik4-elsc__c__b__t__b">
                          {t('liveblog.sort.latest')}
                        </span>
                      </span>
                    </button>
                    <button
                      className={`tik4-elsc__b tik4-button tik4-button-group__button ${currentSort === 'asc' ? 'tik4-button--outline-dark tik4-button-group__button--active' : 'tik4-button--outline-muted'} tik4-button--sm`}
                      onClick={onSortAscClickPreventDefault} type="button" disabled={isLoadingSortChange}>
                      <ArrowDown className="tik4-elsc__b__i tik4-icon tik4-icon--mr"/>
                      <span className="tik4-elsc__c__b__t">
                        <span className="tik4-elsc__c__b__t__d">
                          {t('liveblog.sort.oldest')}
                        </span>
                        <span className="tik4-elsc__c__b__t__b">
                          {t('liveblog.sort.oldest')}
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        {rows.length === 0 ?
          <div className="tik4-no-events">
            {game?.event_tags?.length && selectedTags ? t('liveblog.no_events_for_tag', {tag: game.event_tags?.filter(tag => selectedTags.includes(tag._id!))?.[0]?.name}) : t('liveblog.no_events')}
          </div>
          :
          <MasonryContainer className="tik4-event-list">
            {rows.map((row) =>
              <React.Fragment key={`masonry-${row.local_id}`}>
                <EventExtensionRenderer event={row} Extension={extension?.AboveEvent}/>
                <ErrorBoundary key={row.local_id} FallbackComponent={ErrorComponent}>
                  <LiveblogRowSwitcher
                    game={game}
                    event={row}
                    onMediaClick={onMediaClick}
                    eventExtension={extension}
                    reactionCounts={reactionCounts ? (reactionCounts[row.local_id!] || {}) : undefined}
                  />
                </ErrorBoundary>
                <EventExtensionRenderer event={row} Extension={extension?.BelowEvent}/>
              </React.Fragment>
              )
            }
          </MasonryContainer>
        }

        {loadMoreBottomLink &&
          <div className="tik4-load-more-bottom">
            <a className="tik4-load-more-bottom__button tik4-button tik4-button--primary" href={loadMoreBottomLink}
               onClick={onLoadMoreBottomClickPreventDefault}>
              {isLoadingMoreBottom ?
                <CircleLoader className="tik4-icon tik4-icon--mr tik4-spin"/>
                :
                <ChevronDown className="tik4-icon tik4-icon--mr"/>
              }

              {t('liveblog.load_more_bottom.button')}
            </a>
          </div>
        }

        <div className="tik4-by">
          <a className="tik4-by__link" target="_blank" rel="noreferrer"
             href={`https://tickaroo.com/${inferLanguage(i18n.language)}/liveblog-software?utm_source=${env.clientId || "editor"}&utm_medium=embedjs-comments&utm_term=liveblog-by-tickaroo&utm_campaign=Product%20Link`}>
            <TickarooLogo className="tik4-by__logo" width={55} height={12}/>
            <span className="tik4-by__tickaroo">Tickaroo</span>
            <span className="tik4-by__powered-by">Live Blog Software</span>
          </a>

        </div>
      </div>
    </React.Fragment>
  } else {
    return <React.Fragment>

    </React.Fragment>
  }
}
